import React, { useState } from 'react';
import { useUsers, useCreateUser, useUpdateUser, useDeleteUser, useApproveUser, User } from '../hooks/useUsers';
import { useToastContext } from '../contexts/ToastContext';

const ROLES = [
  { value: 'ADMIN', label: '👑 Administrateur', description: 'Tous les droits' },
  { value: 'GESTIONNAIRE', label: '📋 Gestionnaire', description: 'Gestion complète' },
  { value: 'INVENTORISTE', label: '✅ Inventoriste', description: 'Inventaire et modification' },
  { value: 'LECTEUR', label: '👁️ Lecteur', description: 'Lecture seule' },
];

const UtilisateursPage: React.FC = () => {
  const { showSuccess, showError, showWarning, showInfo } = useToastContext();
  const { data: users = [], isLoading, error } = useUsers();
  const createUserMutation = useCreateUser();
  const updateUserMutation = useUpdateUser();
  const deleteUserMutation = useDeleteUser();
  const approveUserMutation = useApproveUser();

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    password: '',
    role: 'LECTEUR',
    isActive: true,
  });

  const handleOpenCreate = () => {
    setEditingUser(null);
    setFormData({
      email: '',
      name: '',
      password: '',
      role: 'LECTEUR',
      isActive: true,
    });
    setIsCreateModalOpen(true);
  };

  const handleOpenEdit = (user: User) => {
    setEditingUser(user);
    setFormData({
      email: user.email,
      name: user.name || '',
      password: '',
      role: user.role,
      isActive: user.isActive,
    });
    setIsCreateModalOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      if (editingUser) {
        // Modification
        const updateData: any = {
          email: formData.email,
          name: formData.name,
          role: formData.role,
          isActive: formData.isActive,
        };
        
        // N'inclure le password que s'il est fourni
        if (formData.password) {
          updateData.password = formData.password;
        }

        await updateUserMutation.mutateAsync({
          id: editingUser.id,
          data: updateData,
        });
        showSuccess('Utilisateur modifié', 'Les modifications ont été enregistrées.');
      } else {
        // Création
        if (!formData.password) {
          showWarning('Mot de passe requis', 'Le mot de passe est obligatoire pour créer un utilisateur.');
          return;
        }

        await createUserMutation.mutateAsync(formData);
        showSuccess('Utilisateur créé', 'Le compte a été créé avec succès.');
      }

      setIsCreateModalOpen(false);
    } catch (error: any) {
      console.error('Erreur:', error);
      showError('Erreur', error.response?.data?.message || error.message);
    }
  };

  const handleDelete = async (user: User) => {
    if (user.role === 'ADMIN' && users.filter(u => u.role === 'ADMIN').length === 1) {
      showWarning('Suppression impossible', 'Impossible de supprimer le dernier administrateur.');
      return;
    }

    if (window.confirm(`Êtes-vous sûr de vouloir supprimer l'utilisateur "${user.name || user.email}" ?`)) {
      try {
        await deleteUserMutation.mutateAsync(user.id);
        showSuccess('Utilisateur supprimé', 'Le compte a été supprimé avec succès.');
      } catch (error: any) {
        console.error('Erreur:', error);
        showError('Erreur', error.response?.data?.message || error.message);
      }
    }
  };

  const handleToggleActive = async (user: User) => {
    try {
      await updateUserMutation.mutateAsync({
        id: user.id,
        data: { isActive: !user.isActive },
      });
      showSuccess('Statut mis à jour', `Utilisateur ${user.isActive ? 'désactivé' : 'activé'} avec succès.`);
    } catch (error: any) {
      console.error('Erreur:', error);
      showError('Erreur', error.response?.data?.message || error.message);
    }
  };

  const handleApprove = async (user: User) => {
    if (!window.confirm(`Valider le compte de « ${user.name || user.email} » ?`)) return;
    try {
      await approveUserMutation.mutateAsync(user.id);
      showSuccess('Compte validé', 'L\'utilisateur peut se reconnecter.');
    } catch (error: any) {
      showError('Erreur', error.response?.data?.message || error.message);
    }
  };

  const pendingUsers = users.filter((u) => u.validationStatus === 'PENDING');

  const getRoleInfo = (role: string) => {
    return ROLES.find(r => r.value === role) || ROLES[3];
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <h1 className="text-3xl font-bold text-gray-900">Gestion des Utilisateurs</h1>
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <h1 className="text-3xl font-bold text-gray-900">Gestion des Utilisateurs</h1>
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-red-800">Erreur: {error.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Gestion des Utilisateurs</h1>
          <p className="text-gray-600 mt-1">
            {users.length} utilisateur{users.length > 1 ? 's' : ''} enregistré{users.length > 1 ? 's' : ''}
          </p>
        </div>
        <button
          onClick={handleOpenCreate}
          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
        >
          <span>➕</span>
          <span>Nouvel Utilisateur</span>
        </button>
      </div>

      {/* Comptes en attente de validation */}
      {pendingUsers.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <h2 className="text-lg font-semibold text-amber-900 mb-2">
            ⏳ {pendingUsers.length} compte{pendingUsers.length > 1 ? 's' : ''} en attente de validation
          </h2>
          <ul className="space-y-2">
            {pendingUsers.map((user) => (
              <li key={user.id} className="flex flex-wrap items-center justify-between gap-2 bg-white rounded-md px-3 py-2 border border-amber-100">
                <span className="text-sm text-gray-800">
                  <strong>{user.name || user.email}</strong> — {user.email}
                </span>
                <button
                  type="button"
                  onClick={() => handleApprove(user)}
                  className="text-sm bg-green-600 text-white px-3 py-1 rounded-md hover:bg-green-700"
                >
                  ✅ Valider
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Statistiques */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {ROLES.map((role) => {
          const count = users.filter(u => u.role === role.value).length;
          return (
            <div key={role.value} className="bg-white shadow rounded-lg p-4">
              <div className="text-2xl mb-1">{role.label.split(' ')[0]}</div>
              <div className="text-2xl font-bold text-gray-900">{count}</div>
              <div className="text-sm text-gray-600">{role.label.split(' ').slice(1).join(' ')}</div>
            </div>
          );
        })}
      </div>

      {/* Liste des utilisateurs */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Utilisateur
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Rôle
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Statut
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Dernière connexion
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {users.map((user) => {
              const roleInfo = getRoleInfo(user.role);
              return (
                <tr key={user.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center">
                        <span className="text-blue-600 font-medium">
                          {user.name?.charAt(0).toUpperCase() || user.email.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">{user.name || 'Sans nom'}</div>
                        <div className="text-sm text-gray-500">{user.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-900">{roleInfo.label}</span>
                    <div className="text-xs text-gray-500">{roleInfo.description}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {user.validationStatus === 'PENDING' ? (
                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-amber-100 text-amber-800">
                        ⏳ En attente
                      </span>
                    ) : (
                      <button
                        onClick={() => handleToggleActive(user)}
                        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full cursor-pointer ${
                          user.isActive
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {user.isActive ? '✅ Actif' : '❌ Inactif'}
                      </button>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {user.lastLogin ? formatDate(user.lastLogin) : 'Jamais'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    {user.validationStatus === 'PENDING' && (
                      <button
                        onClick={() => handleApprove(user)}
                        className="text-green-600 hover:text-green-900 mr-4"
                      >
                        ✅ Valider
                      </button>
                    )}
                    <button
                      onClick={() => handleOpenEdit(user)}
                      className="text-blue-600 hover:text-blue-900 mr-4"
                    >
                      ✏️ Modifier
                    </button>
                    <button
                      onClick={() => handleDelete(user)}
                      className="text-red-600 hover:text-red-900"
                      disabled={user.role === 'ADMIN' && users.filter(u => u.role === 'ADMIN').length === 1}
                    >
                      🗑️ Supprimer
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {users.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500">Aucun utilisateur trouvé.</p>
          </div>
        )}
      </div>

      {/* Modal Création/Modification */}
      {isCreateModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setIsCreateModalOpen(false)} />

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <form onSubmit={handleSubmit}>
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-medium text-gray-900">
                      {editingUser ? '✏️ Modifier l\'utilisateur' : '➕ Nouvel Utilisateur'}
                    </h3>
                    <button
                      type="button"
                      onClick={() => setIsCreateModalOpen(false)}
                      className="text-gray-400 hover:text-gray-500"
                    >
                      <span className="text-2xl">&times;</span>
                    </button>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Email *
                      </label>
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Nom
                      </label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Mot de passe {editingUser && '(laisser vide pour ne pas changer)'}
                      </label>
                      <input
                        type="password"
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required={!editingUser}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Rôle *
                      </label>
                      <div className="space-y-2">
                        {ROLES.map((role) => (
                          <label
                            key={role.value}
                            className="flex items-center p-3 border border-gray-300 rounded-md hover:bg-gray-50 cursor-pointer"
                          >
                            <input
                              type="radio"
                              name="role"
                              value={role.value}
                              checked={formData.role === role.value}
                              onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                              className="form-radio text-blue-600"
                            />
                            <div className="ml-3">
                              <div className="text-sm font-medium text-gray-900">{role.label}</div>
                              <div className="text-xs text-gray-500">{role.description}</div>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.isActive}
                          onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                          className="form-checkbox text-blue-600"
                        />
                        <span className="text-sm font-medium text-gray-700">
                          Compte actif
                        </span>
                      </label>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    disabled={createUserMutation.isPending || updateUserMutation.isPending}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 disabled:opacity-50 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    {createUserMutation.isPending || updateUserMutation.isPending
                      ? 'Enregistrement...'
                      : editingUser
                      ? 'Modifier'
                      : 'Créer'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsCreateModalOpen(false)}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Annuler
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UtilisateursPage;

